import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions,
  useAuth0,
} from "@auth0/auth0-react";
import { useCallback } from "react";
import { LoadingState, UnauthenticatedState } from "../types";
import { AuthMappedProvider } from "./AuthMappedProvider";
import {
  getAuth0RedirectUri,
  getDeploymentRootUrl,
} from "../../shared/functions";

interface AuthContainerProps extends Auth0ProviderOptions {
  children: React.ReactNode;
  audience: string;
  scope: string;
  setAccessToken: (token: string) => void;
  setTokenProvider: (provider: () => Promise<string>) => void;
  fallback: (state: UnauthenticatedState | LoadingState) => React.ReactNode;
  onLogout?: () => void;
  navigate: (url: string) => void;
  /** Dev-only: inject the AgenticProvisioner role onto the real Auth0 session. */
  addProvisionerRole?: boolean;
}

export function AuthProvider({
  children,
  domain,
  clientId,
  audience,
  scope,
  setAccessToken,
  setTokenProvider,
  fallback,
  onLogout,
  navigate,
  addProvisionerRole,
}: AuthContainerProps) {
  const redirectUri = getAuth0RedirectUri();

  // On localhost over plain HTTP, Auth0's session cookie (SameSite=None;Secure)
  // is never sent in the silent-auth iframe, so tokens can't be restored after
  // a page refresh. Refresh tokens + localStorage cache sidestep the iframe
  // entirely. Anchor to localhost explicitly — any other HTTP origin (misconfigured
  // preview, internal host, on-prem) must not persist refresh tokens in
  // localStorage, where XSS would mean full account compromise.
  // HTTPS deployments (sandbox, preview, prod) use the default in-memory cache
  // with iframe-based silent auth, which works fine over TLS.
  // Requires "Allow Offline Access" to be enabled on the Auth0 API.
  const { protocol, hostname } = window.location;
  const isLocalDev =
    protocol === "http:" &&
    (hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "[::1]");

  // Client-side navigation preserves Auth0's in-memory token across the
  // callback redirect. window.location.replace() would cause a full page
  // reload, losing the token and forcing a silent re-auth that fails on
  // localhost HTTP because SameSite=None;Secure cookies require HTTPS.
  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      // After Auth0 strips the auth params, bounce the user to the URL they
      // were heading to before login.
      //
      // `appState.returnTo` is the full URL captured by the caller (login button,
      // session-expired screen, etc.) and includes the path prefix for the
      // current deployment. If it's missing, fall back to the deployment root
      // recorded in the <base href> tag.
      let redirectUrl = appState?.returnTo || getDeploymentRootUrl();

      if (appState?.invitationToken) {
        const url = new URL(redirectUrl, window.location.origin);
        url.searchParams.set("invitation_token", appState.invitationToken);
        redirectUrl = url.toString();
      }

      navigate(redirectUrl);
    },
    [navigate],
  );

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience,
        scope,
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={isLocalDev}
      cacheLocation={isLocalDev ? "localstorage" : "memory"}
    >
      <AuthMappedProvider
        fallback={fallback}
        useAuth={useAuth0}
        setAccessToken={setAccessToken}
        setTokenProvider={setTokenProvider}
        onLogout={onLogout}
        addProvisionerRole={addProvisionerRole}
      >
        {children}
      </AuthMappedProvider>
    </Auth0Provider>
  );
}
